"use client";

import { animate, motion, useInView } from "motion/react";
import { useEffect, useId, useRef, useState } from "react";

const BAR1_BG_WIDTH = 127;
const BAR1_TARGET_WIDTH = 115;
const BAR2_BG_WIDTH = 125;
const BAR2_TARGET_WIDTH = 28.4091;

export default function EquipmentStatus({
  bar1Label = "On Sites",
  bar1Value = 150,
  bar1Total = 152,
  bar2Label = "Out of Service",
  bar2Value = 2,
  bar2Total = 152,
  duration = 1,
  initialDelay = 0,
  className,
}) {
  const clipId = useId();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [count1, setCount1] = useState(0);
  const [count2, setCount2] = useState(0);

  useEffect(() => {
    if (!isInView) return;

    const controlsRef = { c1: null, c2: null };

    const timeout = setTimeout(() => {
      controlsRef.c1 = animate(0, bar1Value, {
        duration,
        ease: "easeOut",
        onUpdate: (v) => setCount1(Math.round(v)),
      });

      controlsRef.c2 = animate(0, bar2Value, {
        duration,
        ease: "easeOut",
        onUpdate: (v) => setCount2(Math.round(v)),
      });
    }, initialDelay * 1000);

    return () => {
      clearTimeout(timeout);
      controlsRef.c1?.stop();
      controlsRef.c2?.stop();
    };
  }, [isInView, bar1Value, bar2Value, duration, initialDelay]);

  const fontStyle = {
    fontFamily: "system-ui, -apple-system, sans-serif",
    fontVariantNumeric: "tabular-nums",
  };

  return (
    <motion.svg
      ref={ref}
      viewBox="0 0 220 143"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label={`Equipment status: ${bar1Value}/${bar1Total} on site, ${bar2Value}/${bar2Total} out of service`}
      initial={{ scale: 0.9, opacity: 0 }}
      animate={isInView ? { scale: 1, opacity: 1 } : { scale: 0.9, opacity: 0 }}
      // bounce
      transition={{ duration: 0.25, delay: initialDelay, type: "spring", stiffness: 100, mass: 1 }}
    >
      {/* Card shadow + background */}
      <rect x="10" y="10" width="210" height="133" rx="6" fill="#254BE4" />
      <rect width="211" height="131" rx="6" fill="white" />

      <g clipPath={`url(#${clipId})`}>
        {/* Row 1: label */}
        <text x="13" y="29" fontSize="13" fontWeight="500" fill="#434650" style={fontStyle}>
          {bar1Label}
        </text>

        {/* Row 1: progress bar background */}
        <rect x="13" y="47.5" width={BAR1_BG_WIDTH} height="8" rx="4" fill="#F7F7F7" />

        {/* Row 1: animated progress bar fill */}
        <motion.rect
          x="13"
          y="48"
          height="8"
          rx="4"
          fill="#001ADB"
          initial={{ width: 0 }}
          animate={isInView ? { width: BAR1_TARGET_WIDTH } : { width: 0 }}
          transition={{ duration, delay: initialDelay + 0.25, ease: "easeOut" }}
        />

        {/* Row 1: animated counter */}
        <text x="196" y="56" textAnchor="end" fontSize="12" style={fontStyle}>
          <tspan fontWeight="700" fill="#15171E">
            {count1}
          </tspan>
          <tspan fill="#7F818A"> / {bar1Total}</tspan>
        </text>

        {/* Row 2: label */}
        <text x="13" y="85" fontSize="13" fontWeight="500" fill="#434650" style={fontStyle}>
          {bar2Label}
        </text>

        {/* Row 2: progress bar background */}
        <rect x="13" y="103.5" width={BAR2_BG_WIDTH} height="8" rx="4" fill="#F7F7F7" />

        {/* Row 2: animated progress bar fill */}
        <motion.rect
          x="13"
          y="103"
          height="8"
          rx="4"
          fill="#E3755A"
          initial={{ width: 0 }}
          animate={isInView ? { width: BAR2_TARGET_WIDTH } : { width: 0 }}
          transition={{ duration, delay: initialDelay + 0.25, ease: "easeOut" }}
        />

        {/* Row 2: animated counter */}
        <text x="196" y="112" textAnchor="end" fontSize="12" style={fontStyle}>
          <tspan fontWeight="700" fill="#15171E">
            {count2}
          </tspan>
          <tspan fill="#7F818A"> / {bar2Total}</tspan>
        </text>
      </g>

      <defs>
        <clipPath id={clipId}>
          <rect width="199" height="121" fill="white" transform="translate(6 5)" />
        </clipPath>
      </defs>
    </motion.svg>
  );
}
